'use client';

import './article-tabs.scss';

import { t } from 'i18next';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import {
  TabGroup,
  TabGroupProps,
} from '@sola/ark-core/molecules/tab-group/tab-group.js';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.js';
import { ARTICLE_URL_PARAMS } from '@sola/schema/lib/schema.js';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.js';
import { QueryResponsePublished } from '@sola/skippy-search-sdk/lib/schema.js';

import { ArticleControls } from './article-controls';
import { ArticleListProps } from './article-list';
import { ArticleTabsEnum, ArticleTermList } from './article-term-list';

const getTabItems = (
  seoLevel: number,
  articleType: ArticleTypesEnum,
  articleData?: QueryResponsePublished,
): TabGroupProps => {
  const seoLevelTabFirst = seoLevel;
  const seoLevelTab = seoLevel === 1 ? 2 : seoLevel;
  const tabs = {
    title: 'Article Title',
    items: [
      {
        // todo: move to article.tabs
        title: t(`common:skippy.tabs.${ArticleTabsEnum.enum.all}`),
        tabKey: ArticleTabsEnum.enum.all as ArticleTabsEnum,
        body: (
          <ArticleControls
            seoLevel={seoLevelTabFirst + 1}
            articleType={articleType}
          />
        ),
        seoLevel: seoLevelTabFirst,
      },
    ],
  };

  const facetsThemes =
    articleData?.facets.filter(
      (facet) => facet?.field === 'taxonomy.articleThemes.keyword',
    ) || [];
  const tabActiveThemes = facetsThemes.some(
    (facet) => facet?.values && facet.values.length > 3,
  );
  if (tabActiveThemes) {
    tabs.items.push({
      title: t(`common:skippy.tabs.${ArticleTabsEnum.enum.theme}`),
      tabKey: ArticleTabsEnum.enum.theme,
      body: (
        <div className="py-roomy-sm">
          <ArticleTermList
            seoLevel={seoLevelTab + 1}
            tabGroup={ArticleTabsEnum.enum.theme}
            articleType={articleType}
            facets={facetsThemes}
          />
        </div>
      ),
      seoLevel: seoLevelTab,
    });
  }

  const facetsTopics =
    articleData?.facets.filter(
      (facet) => facet?.field === 'taxonomy.articleTopics.keyword',
    ) || [];
  const tabActiveTopics = facetsTopics.some(
    (facet) => facet?.values && facet.values.length > 3,
  );
  if (tabActiveTopics) {
    tabs.items.push({
      title: t(`common:skippy.tabs.${ArticleTabsEnum.enum.topic}`),
      tabKey: ArticleTabsEnum.enum.topic,
      body: (
        <div className="py-roomy-sm">
          <ArticleTermList
            seoLevel={seoLevelTab + 1}
            tabGroup={ArticleTabsEnum.enum.topic}
            articleType={articleType}
            facets={facetsTopics}
          />
        </div>
      ),
      seoLevel: seoLevelTab,
    });
  }

  const facetsCountry =
    articleData?.facets.filter(
      (facet) => facet?.field === 'taxonomy.programCountry.keyword',
    ) || [];
  const tabActiveCountry = facetsCountry.some(
    (facet) => facet?.values && facet.values.length > 3,
  );
  if (tabActiveCountry) {
    tabs.items.push({
      title: t(`common:skippy.tabs.${ArticleTabsEnum.enum.country}`),
      tabKey: ArticleTabsEnum.enum.country,
      body: (
        <div className="py-roomy-sm">
          <ArticleTermList
            seoLevel={seoLevelTab + 1}
            tabGroup={ArticleTabsEnum.enum.country}
            articleType={articleType}
            facets={facetsCountry}
          />
        </div>
      ),
      seoLevel: seoLevelTab,
    });
  }

  // @TODO: bring back when content hub search is supported
  // tabs.items.push({
  //   title: t(`skippy.tabs.${ArticleTabsEnum.Enum.search}`),
  //   tabKey: ArticleTabsEnum.Enum.search,
  //   body: (
  //     <div className="py-roomy-sm">
  //       <SearchBar
  //         title={t('skippy.article.searchContentHub')}
  //         placeholder={t('skippy.article.searchPlaceholder')}
  //         seoLevel={seoLevelTab + 1}
  //       />
  //     </div>
  //   ),
  //   icon: 'search-sm',
  //   seoLevel: seoLevelTab,
  // });

  return tabs;
};

export const ArticleTabs: React.FC<
  ArticleListProps & { articleType: ArticleTypesEnum }
> = ({ seoLevel = 2, articleType }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const params = useMemo(
    () => new URLSearchParams(searchParams?.toString()),
    [searchParams],
  );

  const clearUnrelatedParams = () => {
    params.set(ARTICLE_URL_PARAMS.PAGE, '0'); // reset page to 1 on tab change
    params.delete(ARTICLE_URL_PARAMS.COUNTRY);
    params.delete(ARTICLE_URL_PARAMS.THEME);
  };

  // TEMP - just trying to more centrally getting facet info
  // @TODO Move the logic of molecules/article-list to a more parent logic-filled part organism (/organisms/article-controls)
  // handle the tab variant based on the props
  const articleFacetQuery = useListArticlesQuery({
    perPage: 1,
    filters: JSON.stringify({
      'taxonomy.articleType.keyword': articleType || undefined,
    }),
  });
  const { data: articleData, isLoading, isError } = articleFacetQuery;

  const tabItems = getTabItems(seoLevel, articleType, articleData);

  if (tabItems.items?.length === 1) {
    return tabItems.items[0].body;
  }

  return (
    <TabGroup
      {...tabItems}
      activeValue={
        params?.get(ARTICLE_URL_PARAMS.TAB) || ArticleTabsEnum.enum.all
      }
      onChange={(v: string) => {
        clearUnrelatedParams();
        params.set(ARTICLE_URL_PARAMS.TAB, v);
        router.replace(`${pathname}?${params.toString()}`, { scroll: false });
      }}
    />
  );
};

/**
 * Skeleton component for ArticleTabs.
 * Matches the dimensions and layout of the ArticleTabs component.
 */
export const ArticleTabsSkeleton = () => (
  <div className="TabGroup">
    {/* Tab navigation skeleton */}
    <div className="flex gap-snug-sm mb-comfy-lg">
      <Skeleton height={40} width={80} borderRadius="0.5rem" />
      <Skeleton height={40} width={100} borderRadius="0.5rem" />
      <Skeleton height={40} width={90} borderRadius="0.5rem" />
      <Skeleton height={40} width={110} borderRadius="0.5rem" />
    </div>

    {/* Tab content skeleton */}
    <div className="py-roomy-sm">
      {/* ArticleFilter skeleton */}
      <div className="flex flex-col md:flex-row md:justify-between md:align-center gap-comfy-lg mb-comfy-xl">
        <Skeleton height={20} width={120} />
        <div className="flex gap-comfy-sm">
          <Skeleton height={40} width={140} borderRadius="0.5rem" />
          <Skeleton height={40} width={80} borderRadius="0.5rem" />
        </div>
      </div>

      {/* Article cards grid skeleton */}
      <div className="grid gap-comfy-sm @md:gap-comfy-lg grid-cols-1 @sm:grid-cols-2 @md:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="flex flex-col gap-comfy-sm">
            {/* Image skeleton */}
            <Skeleton
              width="100%"
              height="auto"
              style={{ aspectRatio: '16/9' }}
              borderRadius="0.25rem"
            />
            {/* Eyebrow skeleton */}
            <Skeleton height={18} width="40%" />
            {/* Title skeleton */}
            <div className="flex flex-col gap-snug-xs">
              <Skeleton height={24} width="100%" />
              <Skeleton height={24} width="80%" />
            </div>
            {/* Summary skeleton */}
            <div className="flex flex-col gap-snug-xs">
              <Skeleton height={16} width="100%" />
              <Skeleton height={16} width="90%" />
            </div>
          </div>
        ))}
      </div>

      {/* Pagination skeleton */}
      <div className="flex justify-center mt-comfy-lg pt-comfy-lg border-t border-neutral-mild">
        <div className="flex gap-snug-sm">
          <Skeleton height={48} width={48} borderRadius="0.5rem" />
          <Skeleton height={48} width={48} borderRadius="0.5rem" />
          <Skeleton height={48} width={48} borderRadius="0.5rem" />
        </div>
      </div>
    </div>
  </div>
);
